import { GeoLocation } from "./geoLocation";

export type ILocation = {
    location_id: string;
    location_name: string;
    maps_url: string;
    currency: string;
    tags: string[];
    latitude?: string;
    longitude?: string;
    distance?: number;
    address: {
        line_1?: string;
        line_2?: string;
        city?: string;
        zip?: string;
        country?: string;
        region?: string;
    };
    booking_business?: string;
    service_id?: string;
    timezone?: string;
    timezone_name?: string;
    is_test_gym?: string;
    is_pricing_panel_enabled?: boolean;
    country_code?: string;
};

export const getBookingClusterForGym = (tags: string[]) => {
    const clusterTag = tags.find((tag) => tag.startsWith('booking_cluster_'));

    if (!clusterTag) {
        return '';
    }

    return clusterTag;
};

export const findGymsWithSameCluster = (
    locations: ILocation[],
    clusterTag: string,
    location_id: string
): ILocation[] => {
    return locations.filter((loc) => {
        const tags = loc.tags || [];
        const hasCluster = tags.includes(clusterTag);
        const isNotExcluded = loc.location_id !== location_id;
        return hasCluster && isNotExcluded;
    });
};

export const sortByDistanceToMainGym = (
    locations: ILocation[],
    mainGym: Partial<ILocation>
): (ILocation & { distance: number })[] => {
    return locations
        .map((location) => {
            const distance = calculateDistanceInMiles(
                parseFloat(mainGym.latitude ?? '0'),
                parseFloat(mainGym.longitude ?? '0'),
                parseFloat(location.latitude ?? '0'),
                parseFloat(location.longitude ?? '0')
            );

            return { ...location, distance };
        })
        .sort((a, b) => a.distance - b.distance);
};

/**
 * Calculates the distance between two points on Earth in miles.
 * @param lat1 - Latitude of the first point
 * @param lon1 - Longitude of the first point
 * @param lat2 - Latitude of the second point
 * @param lon2 - Longitude of the second point
 * @returns The distance between the two points in miles.
 */
export const calculateDistanceInMiles = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number => {
    const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;
    const R = 3958.8; // Earth's radius in miles
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRadians(lat1)) *
            Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c;

    return Math.round(distance * 100) / 100;
};

export const sortGymsAlphabetically = (
    locations: ILocation[]
): (ILocation & { distance: number })[] => {
    return locations
        .map((location) => ({
            ...location,
            distance: 0,
        }))
        .sort((a, b) =>
            a.location_name.localeCompare(b.location_name, undefined, {
                sensitivity: 'base',
            })
        );
};

const isLocationInIndia = ({ latitude, longitude }: GeoLocation): boolean => {
    // Bounding box covering mainland India and islands (Andaman & Nicobar, Lakshadweep)
    return (
        latitude >= 6.75 &&
        latitude <= 37.10 &&
        longitude >= 68.11 &&
        longitude <= 97.42
    );
};

export const excludeTestGyms = (allGyms: ILocation[], userLocation: GeoLocation, showTestGym: boolean = false) => {  
    if (showTestGym && isLocationInIndia(userLocation)) {
        return allGyms;
    }
    return allGyms.filter((gym) => !gym.is_test_gym);
};
